/**
 * HVP API Client - error normalization, stats and utilities
 *
 * handleError() classification is consumed by the no-match re-check
 * (transport reason), so its type strings are part of the contract.
 *
 * Split out of api-client.js (350-line rule): the methods below moved
 * VERBATIM and are installed on HVPApiClient.prototype by
 * api-client.js, so `this` is the same instance and behavior is identical.
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

export const ApiDiagnosticsMethods = {
    /**
     * Handle API error
     *
     * @param {Error} error - Error object
     * @returns {Object} Normalized error
     */
    handleError(error) {
        const normalized = {
            message: error.message || 'Unknown error',
            type: 'unknown',
            recoverable: true
        };

        if (error.name === 'AbortError') {
            normalized.type = 'cancelled';
            normalized.message = 'Request cancelled';
            normalized.recoverable = false;

        } else if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
            normalized.type = 'network';
            normalized.message = 'Network error - please check your connection';
            normalized.recoverable = true;

        } else if (error.message.includes('timeout')) {
            normalized.type = 'timeout';
            normalized.message = 'Request timed out - please try again';
            normalized.recoverable = true;

        } else if (error.message.includes('HTTP 403')) {
            // Expired/invalid nonce - almost always a stale page served from
            // cache to an anonymous visitor. Recoverable by reloading (fresh nonce).
            normalized.type = 'auth';
            normalized.message = 'Your session expired - please reload and try again';
            normalized.recoverable = true;

        } else if (error.message.includes('HTTP 4')) {
            normalized.type = 'client';
            normalized.recoverable = false;

        } else if (error.message.includes('HTTP 5')) {
            normalized.type = 'server';
            normalized.message = 'Server error - please try again later';
            normalized.recoverable = true;
        }

        return normalized;
    },

    /**
     * Get API statistics
     *
     * @returns {Object} Statistics
     */
    getStats() {
        return {
            ...this.stats,
            activeRequests: this.activeRequests.size,
            errorRate: this.stats.requests > 0
                ? ((this.stats.errors / this.stats.requests) * 100).toFixed(2)
                : 0
        };
    },

    /**
     * Generate unique request ID
     *
     * @private
     */
    generateRequestId() {
        return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    },

    /**
     * Sleep utility
     *
     * @private
     */
    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    },

    /**
     * Log message (debug mode only)
     *
     * @private
     */
    log(...args) {
        if (this.debug) {
            window.hvpData && window.hvpData.debug && console.log(
                '%c[HVP API]',
                'background: #3b82f6; color: #fff; padding: 2px 6px; border-radius: 3px; font-weight: bold;',
                ...args
            );
        }
    },
};

export default ApiDiagnosticsMethods;
